/**
 * TOSS Web Application - DropdownPortal Component
 *
 * Portal-based dropdown component that renders outside the DOM hierarchy.
 * This solves z-index stacking context issues when dropdowns need to
 * appear above sticky/fixed positioned parents.
 *
 * Features:
 * - Renders in document.body via React Portal
 * - Dynamic positioning relative to trigger element
 * - Click outside detection
 * - Escape key to close
 * - Scroll and resize repositioning
 */

import { createPortal } from 'react-dom';
import {
  useRef,
  useEffect,
  useState,
  useCallback,
  type RefObject,
  type ReactNode,
} from 'react';
import { useClickOutside } from '@/hooks/useClickOutside';
import { EDGE_GAP, resolveHorizontalPosition } from './dropdownPosition';

// ============================================================================
// Types
// ============================================================================

/** Ниже этого слой уже не список, а щель, — лучше показать хотя бы две строки с прокруткой. */
const MIN_LAYER_HEIGHT = 96;
/**
 * Сколько места снизу достаточно, чтобы открыться ВНИЗ.
 *
 * Не «где места больше»: в листе сверху почти всегда просторнее, и список честно уезжал вверх —
 * накрывая собой поля, которые только что заполняли. Вниз — направление по умолчанию; вверх
 * только когда снизу не помещаются и три строки.
 */
const PREFERRED_SPACE = 160;

export interface DropdownPortalProps {
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Callback when dropdown should close */
  onClose: () => void;
  /** Reference to the trigger element for positioning */
  triggerRef: RefObject<HTMLElement | null>;
  /** Dropdown content */
  children: ReactNode;
  /** Additional CSS classes for the dropdown container */
  className?: string;
  /** Placement relative to trigger */
  placement?: 'bottom-end' | 'bottom-start';
  /**
   * Ширина всплывающего слоя равна ширине триггера, и он прижат к его левому краю.
   *
   * Для меню это не нужно — кебаб узкий, а его меню шире. Для выпадающего СПИСКА нужно: список
   * значений поля читается как продолжение самого поля, и список уже или шире поля выглядит как
   * чужой слой поверх формы (`Select`, 27.08.2026).
   */
  matchTriggerWidth?: boolean;
  /**
   * Роль слоя. По умолчанию `menu` — форма, в которой портал жил с самого начала и в которой его
   * читают все кебабы. `Select` передаёт `listbox`: у списка значений своя семантика, и `menu`
   * заставил бы скринридер называть выбор значения командой.
   */
  role?: 'menu' | 'listbox';
  /** Нужен, когда на слой ссылается `aria-controls` или `aria-activedescendant` триггера. */
  id?: string;
  /**
   * Слой сам выбирает сторону и сам ограничивает свою высоту свободным местом.
   *
   * Для выпадающего СПИСКА внутри листа это единственное рабочее поведение: лист занимает низ
   * экрана, снизу от поля места почти никогда нет, и «открыться вверх» означало накрыть собой
   * поля, которые заполняют выше (стенд, 27.08.2026). Теперь сторона выбирается по тому, где
   * места больше, а высота ужимается до этого места — список прокручивается внутри себя вместо
   * того, чтобы залезать на форму.
   */
  autoFit?: boolean;
  /**
   * Open ABOVE the trigger instead of below it.
   *
   * Off by default, so every existing call site keeps the behaviour it has always had. The
   * client portal's account menu turns it on: its trigger is the last thing in the sidebar, a
   * few pixels off the bottom of the window, and a list opened downward from there starts
   * below the viewport entirely.
   *
   * Anchored by `bottom` rather than by `top` minus a measured height — the height is not known
   * until after the first paint, and a menu that jumps once it measures itself is worse than
   * one that never moves.
   */
  openUpwards?: boolean;
  /** Offset from trigger in pixels */
  offset?: number;
}

/**
 * Видимая часть окна в координатах layout viewport — та система, в которой считает
 * `getBoundingClientRect()` и позиционируется `position: fixed`.
 *
 * Без клавиатуры это `0 … window.innerHeight`, и вся арифметика ниже остаётся прежней дословно.
 * С клавиатурой — нет: на iOS layout viewport под неё НЕ уменьшается, `window.innerHeight` не
 * меняется, и слой, ужатый по нему, наполовину уезжает под клавиатуру. Настоящий низ показывает
 * `visualViewport`, а `offsetTop` добавляет сдвиг, которым iOS поднимает страницу, чтобы показать
 * поле в фокусе.
 *
 * Возвращает границы, а не высоту: слой выбирает СТОРОНУ, и верх ему нужен так же, как низ.
 */
function visibleBounds(): { top: number; bottom: number } {
  const viewport = typeof window !== 'undefined' ? window.visualViewport : null;
  if (!viewport) return { top: 0, bottom: window.innerHeight };
  const offsetTop = viewport.offsetTop ?? 0;
  return { top: offsetTop, bottom: offsetTop + viewport.height };
}

// ============================================================================
// Component
// ============================================================================

/**
 * Portal-based dropdown that renders in document.body
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 *
 * <button ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
 *   Open Menu
 * </button>
 *
 * <DropdownPortal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   triggerRef={triggerRef}
 *   className="app-surface app-surface-outlined shadow-[var(--shadow-overlay)]"
 * >
 *   <MenuItem>Option 1</MenuItem>
 *   <MenuItem>Option 2</MenuItem>
 * </DropdownPortal>
 * ```
 */
export function DropdownPortal({
  isOpen,
  onClose,
  triggerRef,
  children,
  className = '',
  placement = 'bottom-end',
  matchTriggerWidth = false,
  role = 'menu',
  id,
  autoFit = false,
  openUpwards = false,
  offset = 8,
}: DropdownPortalProps): JSX.Element | null {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<React.CSSProperties>({});

  // Calculate position relative to trigger with bounds checking
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const dropdownEl = dropdownRef.current;
    const dropdownWidth = dropdownEl?.offsetWidth ?? 0;

    // Свободное место меряется по ВИДИМОЙ части окна (см. `visibleBounds`), а не по
    // `window.innerHeight`: под клавиатурой это разные числа, и по второму слой ужимается в
    // высоту, часть которой под клавиатурой и лежит.
    const visible = visibleBounds();

    // One of the two, never both: `bottom` measured from the window's foot when opening upward,
    // `top` from the trigger's foot otherwise.
    const spaceBelow = visible.bottom - rect.bottom - offset - EDGE_GAP;
    const spaceAbove = rect.top - visible.top - offset - EDGE_GAP;
    const upward = autoFit
      ? spaceBelow < PREFERRED_SPACE && spaceAbove > spaceBelow
      : openUpwards;

    // А вот САМИ координаты — по-прежнему от layout viewport: слой `fixed`, и отсчитывается он
    // именно от него. Подставить сюда видимый низ значило бы сдвинуть слой на высоту клавиатуры
    // второй раз.
    const vertical: React.CSSProperties = upward
      ? { bottom: window.innerHeight - rect.top + offset }
      : { top: rect.bottom + offset };

    if (autoFit) {
      vertical.maxHeight = Math.max(MIN_LAYER_HEIGHT, Math.round(upward ? spaceAbove : spaceBelow));
    }

    // Ширина в ширину триггера — тогда горизонталь известна точно и обрезать нечего.
    if (matchTriggerWidth) {
      setPosition({ ...vertical, left: rect.left, width: rect.width });
      return;
    }

    // Объект заменяется целиком, поэтому `left` от прошлого расчёта не переживает переход на
    // `right` и наоборот — гасить противоположный край через `undefined` не требуется.
    setPosition({
      ...vertical,
      ...resolveHorizontalPosition({
        placement,
        triggerLeft: rect.left,
        triggerRight: rect.right,
        dropdownWidth,
        viewportWidth,
      }),
    });
  }, [triggerRef, placement, offset, matchTriggerWidth, openUpwards, autoFit]);

  // Update position when open or on scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    // Re-calculate after the dropdown renders and has a measured width
    const rafId = requestAnimationFrame(updatePosition);

    // Listen to scroll on all ancestors (capture phase)
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    // Появление клавиатуры не даёт ни того ни другого события на iOS — layout viewport не
    // меняется. Пересчитать слой заставляет только визуальный вьюпорт.
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', updatePosition);
    viewport?.addEventListener('scroll', updatePosition);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
      viewport?.removeEventListener('resize', updatePosition);
      viewport?.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, updatePosition]);

  // Close on click outside
  useClickOutside([dropdownRef, triggerRef], onClose, isOpen);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;

    /**
     * Escape закрывает ВЕРХНИЙ слой, и только его.
     *
     * Слушать приходится в фазе ЗАХВАТА и глушить событие: под слоем обычно стоит лист или
     * модальное окно, которые слушают Escape на том же документе. Без этого один Escape закрывал
     * список роли ВМЕСТЕ с формой сотрудника — вместе с тем, что в ней успели набрать
     * (найдено на стенде 27.08.2026).
     */
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };

    // Именно `window` и именно захват: цепочка начинается с window, поэтому этот обработчик
    // отрабатывает раньше любого, кто слушает на документе, — а на документе слушают и `vaul`,
    // и Radix под ним.
    window.addEventListener('keydown', handleEscape, true);
    return () => window.removeEventListener('keydown', handleEscape, true);
  }, [isOpen, onClose]);

  // Don't render if not open
  if (!isOpen) return null;

  return createPortal(
    <div
      ref={dropdownRef}
      id={id}
      className={`fixed z-[9000] ${className}`}
      /**
       * `pointerEvents: 'auto'` — не украшение, а условие работоспособности внутри листа или
       * модального окна.
       *
       * `vaul` (и Radix под ним) на время показа гасит ввод всему, что вне своего слоя, ставя
       * `pointer-events: none` прямо на `<body>`. Слой возвращает себе `auto`, а слой этот —
       * контент листа; портал живёт рядом, в том же `<body>`, и наследовал запрет: список
       * открывался, был виден и НЕ НАЖИМАЛСЯ вообще (стенд, 27.08.2026 — «дропдаун не работает
       * вообще»). Собственное `auto` возвращает ввод именно этому слою и никому больше.
       */
      style={{ ...position, pointerEvents: 'auto' }}
      role={role}
      aria-orientation="vertical"
    >
      {children}
    </div>,
    document.body
  );
}

export default DropdownPortal;
